
/**
 * Module dependencies.
 */

import { color, media, units } from 'src/styles/utils';
import { ifProp, theme } from 'styled-tools';
import Fill from 'src/components/core/layout/fill';
import IconButton from 'src/components/core/buttons/icon-button';
import Image from 'src/components/core/image';
import ModalPortal from 'src/components/core/modals/modal-portal';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import Slider, { Slide } from 'src/components/core/slider';
import SliderNavigation from 'src/components/core/slider/slider-navigation';
import SliderPagination from 'src/components/core/slider/slider-pagination';
import Type from 'src/components/core/typography';
import VideoPlayer from 'src/components/core/video-player';
import closeIcon from 'src/assets/svg/close.svg';
import map from 'lodash/map';
import size from 'lodash/size';
import styled, { createGlobalStyle, css } from 'styled-components';
import swiperStyles from 'swiper/swiper-bundle.css';

/**
 * Export `Item` type.
 */

export type Item = {
  label?: string | null,
  type: 'image',
  url: string
} | {
  coverImage?: string | null
  label?: string | null,
  type: 'video',
  url: string
};

/**
 * `Props` type.
 */

type Props = {
  initialSlide?: number,
  isOpen: boolean,
  items: Item[],
  onClose: () => void
};

/**
 * Export `GlobalSwiperStyle` component.
 */

const GlobalSwiperStyle = createGlobalStyle`
  ${swiperStyles}
`;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled(Fill)<{ visible: boolean }>`
  background-color: ${color('white')};
  display: grid;
  grid-template-areas:
    'closeButton title   .'
    'gallery     gallery gallery'
    'dots        dots    dots';
  grid-template-columns: ${units(5)} 1fr ${units(5)};
  grid-template-rows: ${units(5)} 1fr ${units(5)};
  outline: none;
  padding: ${units(4)} ${theme('grid.gutterMobile')}px;
  position: fixed !important;
  transition: visibility ${theme('animations.defaultTransition')};
  z-index: ${theme('zIndex.modal')};

  ${ifProp('visible', css`
    animation: ${theme('keyframes.fadeIn')} ${theme('animations.defaultTransition')} both;
    visibility: visible;
  `, css`
    animation: ${theme('keyframes.fadeOut')} ${theme('animations.defaultTransition')} both;
    pointer-events: none;
    visibility: hidden;
  `)}

  ${media.min('ms')`
    grid-template-areas:
      '. title   closeButton'
      '. gallery .'
      '. dots    .';
    padding: ${units(4)};
  `}

  ${media.min('md')`
    grid-template-areas:
    '. .    title   .    closeButton'
    '. prev gallery next .'
    '. .    dots    .    .';
    grid-template-columns: ${units(5)} 1fr 10fr 1fr ${units(5)};
  `}

  ${media.min('xl')`
    grid-template-columns: ${units(5)} 2fr 8fr 2fr ${units(5)};
  `}
`;

/**
 * `CloseButton` styled component.
 */

const CloseButton = styled(IconButton).attrs({
  icon: closeIcon,
  iconSize: units(5)
})`
  grid-area: closeButton;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.Paragraph).attrs({ as: 'div' })`
  align-self: center;
  grid-area: title;
  justify-self: center;
`;

/**
 * `StyledSlider` styled component.
 */

const StyledSlider = styled(Slider)`
  grid-area: gallery;
  overflow: hidden;
  padding: ${units(7)} 0 ${units(7.5)};
  width: 100%;

  * {
    outline: none;
  }

  @media not all and (min-resolution: 0.001dpcm) {
    @media {
      height: calc(100vh - ${units(18)});
    }
  }
`;

/**
 * `SliderPaginationWrapper` styled component.
 */

const SliderPaginationWrapper = styled.div`
  grid-area: dots;
  justify-self: center;
`;

/**
 * `PreviousButtonWrapper` styled component.
 */

const PreviousButtonWrapper = styled.div`
  align-self: center;
  grid-area: prev;
  justify-self: end;
  padding: ${units(4)};

  ${media.max('md')`
    display: none;
  `}
`;

/**
 * `NextButtonWrapper` styled component.
 */

const NextButtonWrapper = styled.div`
  align-self: center;
  grid-area: next;
  justify-self: start;
  padding: ${units(4)};

  ${media.max('md')`
    display: none;
  `}
`;

/**
 * `SlideWrapper` styled component.
 */

const SlideWrapper = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  position: relative;

  &::after {
    border: 1px solid ${color('white')};
    bottom: 0;
    content: '';
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

/**
 * `Lightbox` component.
 */

const Lightbox = (props: Props): ReactElement => {
  const { initialSlide, isOpen, items, onClose } = props;
  const total = size(items);
  const modalRef = useRef<HTMLDivElement>();
  const sliderRef = useRef<any>();
  const [activeSlide, setActiveSlide] = useState<number>(initialSlide ?? 0);
  const handleSlideChange = useCallback(({ activeIndex }) => {
    setActiveSlide(activeIndex);
  }, []);

  const handleClickDot = useCallback((index: number) => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.swiper.slideTo(index);
    }
  }, []);

  const handlePrevious = useCallback(() => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.swiper.slidePrev();
    }
  }, []);

  const handleNext = useCallback(() => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.swiper.slideNext();
    }
  }, []);

  useEffect(() => {
    if (isOpen && modalRef && modalRef?.current) {
      const timeout = setTimeout(() => modalRef.current.focus(), 500);

      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && sliderRef && sliderRef.current && initialSlide) {
      sliderRef.current.swiper.slideTo(initialSlide, false, false);
    }
  }, [initialSlide, isOpen]);

  useEffect(() => {
    if (isOpen && sliderRef && sliderRef.current) {
      sliderRef.current.swiper.update();
    }
  }, [isOpen]);

  return (
    <ModalPortal isOpen={isOpen}>
      <GlobalSwiperStyle />

      <Wrapper
        aria-hidden={!isOpen}
        aria-modal
        ref={modalRef}
        role={'dialog'}
        tabIndex={isOpen ? -1 : undefined}
        visible={isOpen}
      >
        <CloseButton onClick={onClose} />

        <Title>
          {items[activeSlide]?.label}
        </Title>

        <StyledSlider
          autoplay={false}
          onSlideChange={handleSlideChange}
          ref={sliderRef}
        >
          {map(items, ({ coverImage, label, type, url }, index: number) => (
            <Slide key={index}>
              <SlideWrapper>
                {type === 'image' && (
                  <Image
                    alt={label}
                    layout={'fill'}
                    objectFit={'contain'}
                    src={url}
                  />
                )}

                {type === 'video' && (
                  <VideoPlayer
                    image={coverImage}
                    playing={index === activeSlide && isOpen}
                    url={url}
                  />
                )}
              </SlideWrapper>
            </Slide>
          ))}
        </StyledSlider>

        {total > 1 && (
          <>
            <SliderPaginationWrapper>
              <SliderPagination
                currentSlide={activeSlide}
                onClickDot={handleClickDot}
                totalSlides={total}
              />
            </SliderPaginationWrapper>

            <PreviousButtonWrapper>
              {total > 1 && (
                <SliderNavigation
                  disabled={activeSlide === 0}
                  onClick={handlePrevious}
                  type={'previous'}
                />
              )}
            </PreviousButtonWrapper>

            <NextButtonWrapper>
              <SliderNavigation
                disabled={activeSlide === total - 1}
                onClick={handleNext}
                type={'next'}
              />
            </NextButtonWrapper>
          </>
        )}
      </Wrapper>
    </ModalPortal>
  );
};

/**
 * Default props.
 */

Lightbox.defaultProps = {
  name: 'lightbox'
};

/**
 * Export `Lightbox` component.
 */

export default Lightbox;
